// Pure password generation + strength math. No DOM, no network. Never logs the
// generated value. Randomness is crypto.getRandomValues with rejection sampling
// so there is no modulo bias.

const LOWER = 'abcdefghijklmnopqrstuvwxyz';
const UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const DIGITS = '0123456789';
const SYMBOLS = '!@#$%^&*()-_=+[]{};:,.?/';
const SIMILAR = new Set(['I', 'l', '1', 'O', '0']);

const strip = (s, excludeSimilar) =>
  excludeSimilar ? [...s].filter((c) => !SIMILAR.has(c)).join('') : s;

/** Per-class character sets for the enabled classes, after excludeSimilar. */
export function classSets({ lower, upper, digits, symbols, excludeSimilar = false } = {}) {
  const sets = [];
  if (upper) sets.push(strip(UPPER, excludeSimilar));
  if (lower) sets.push(strip(LOWER, excludeSimilar));
  if (digits) sets.push(strip(DIGITS, excludeSimilar));
  if (symbols) sets.push(strip(SYMBOLS, excludeSimilar));
  return sets.filter((s) => s.length > 0);
}

/** Combined character pool string for the enabled classes. */
export function buildPool(opts = {}) {
  return classSets(opts).join('');
}

/** Uniform random integer in [0, max) via rejection sampling (no modulo bias). */
function randInt(max) {
  if (max <= 0) throw new Error('randInt: max must be > 0');
  const limit = 256 - (256 % max);
  const buf = new Uint8Array(1);
  let b;
  do {
    crypto.getRandomValues(buf);
    b = buf[0];
  } while (b >= limit);
  return b % max;
}

const pick = (str) => str[randInt(str.length)];

export function generatePassword(opts = {}) {
  const length = opts.length ?? 20;
  const sets = classSets(opts);
  if (sets.length === 0) throw new Error('generatePassword: no character classes enabled');
  if (length < sets.length) {
    throw new Error('generatePassword: length too small to include every enabled class');
  }
  const pool = sets.join('');

  const chars = [];
  // One guaranteed character from each enabled class...
  for (const set of sets) chars.push(pick(set));
  // ...then fill the remainder from the full pool.
  for (let i = sets.length; i < length; i++) chars.push(pick(pool));
  // Fisher-Yates shuffle so the guaranteed chars aren't always at the front.
  for (let i = chars.length - 1; i > 0; i--) {
    const j = randInt(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
}

export function estimateEntropyBits({ length, poolSize }) {
  // Upper bound: actual entropy is marginally lower because of the per-class
  // guarantee in generatePassword, but this is plenty for a strength bar.
  if (!(length > 0) || !poolSize || poolSize < 2) return 0;
  return length * Math.log2(poolSize);
}

export function strengthLabel(bits) {
  if (bits < 40) return 'weak';
  if (bits < 60) return 'fair';
  if (bits < 80) return 'strong';
  return 'very-strong';
}
